import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";

const LoadingContext = createContext(null);

// One overlay survives transitions between authorization, exam data, and image
// preflight. Descendants register pending work through the same ExamLoader API.
export function ExamLoadingBoundary({ children }) {
  const [pending, setPending] = useState(() => new Set());
  const [holding, setHolding] = useState(true);
  const startedAt = useRef(Date.now());
  const register = useCallback(token => {
    setPending(previous => new Set(previous).add(token));
    return () => setPending(previous => {
      const next = new Set(previous);
      next.delete(token);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (pending.size) {
      if (startedAt.current === null) startedAt.current = Date.now();
      setHolding(true);
      return;
    }

    let frame1;
    let frame2;
    // Avoid a brief loader flash on fast loads. Give newly mounted exam widgets
    // two frames to lay out under the overlay before revealing the page.
    const timer = setTimeout(() => {
      frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => {
          startedAt.current = null;
          setHolding(false);
        });
      });
    }, Math.max(0, 300 - (Date.now() - (startedAt.current ?? Date.now()))));
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [pending]);

  const busy = pending.size > 0 || holding;
  return (
    <LoadingContext.Provider value={register}>
      <div aria-hidden={busy ? true : undefined} style={{ visibility: busy ? "hidden" : "visible" }}>
        {children}
      </div>
      {busy && <div style={{ position: "fixed", inset: 0, zIndex: 20000 }}>
        <ExamLoader standalone />
      </div>}
    </LoadingContext.Provider>
  );
}

export default function ExamLoader({ message = "Loading your exam…", standalone = false }) {
  const register = useContext(LoadingContext);
  const token = useRef(null);
  if (token.current === null) token.current = {};
  useLayoutEffect(() => {
    if (register && !standalone) return register(token.current);
  }, [register, standalone]);

  // The boundary owns the visible loader, so phase changes never replace it.
  if (register && !standalone) return null;
  return (
    <div role="status" aria-live="polite" style={styles.wrap}>
      <div style={styles.card}>
        <div aria-hidden="true" style={styles.spinner} />
        <div style={styles.text}>{message}</div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    padding: 24,
    display: "grid",
    placeItems: "center",
    background: "#f6f7f9"
  },
  card: {
    background: "#fff",
    padding: "28px 32px",
    borderRadius: 10,
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16
  },
  text: {
    fontSize: 16,
    color: "#444"
  },
  spinner: {
    width: 36,
    height: 36,
    border: "4px solid #e0e0e0",
    borderTopColor: "#555",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  }
};
